/**
 * WebSocket hook for case-specific email notifications.
 *
 * Connects to /ws/case/{caseId}/ to receive real-time updates
 * when inbound emails are received and processed.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import type { ExtractedBookingData } from './types';

export type EmailNotificationStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'error';

export interface EmailNotificationState {
  status: EmailNotificationStatus;
  emailReceived: boolean;
  emailSubject: string | null;
  emailFrom: string | null;
  isExtracting: boolean;
  extractedData: ExtractedBookingData | null;
  error: string | null;
  errorDetails: {
    reason: string;
    attachmentsFound: Array<{
      filename: string;
      category: string;
      type: string;
    }>;
  } | null;
}

const initialState: EmailNotificationState = {
  status: 'disconnected',
  emailReceived: false,
  emailSubject: null,
  emailFrom: null,
  isExtracting: false,
  extractedData: null,
  error: null,
  errorDetails: null,
};

export function useEmailNotification(caseId: string | null) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const [state, setState] = useState<EmailNotificationState>(initialState);

  const connect = useCallback(() => {
    if (!caseId) {
      console.warn('[EmailNotification] Cannot connect without caseId');
      return;
    }

    // Don't connect if already connected or connecting
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.CONNECTING ||
        wsRef.current.readyState === WebSocket.OPEN)
    ) {
      console.log('[EmailNotification] Already connected or connecting');
      return;
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsHost = import.meta.env.VITE_WS_HOST || window.location.host;
    const wsUrl = `${wsProtocol}://${wsHost}/ws/case/${caseId}/`;

    console.log(`[EmailNotification] Connecting to ${wsUrl}`);
    setState((s) => ({ ...s, status: 'connecting' }));

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[EmailNotification] Connected');
      setState((s) => ({ ...s, status: 'connected' }));
      reconnectAttempts.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[EmailNotification] Received:', data.type, data);

        switch (data.type) {
          case 'inbound_email_received':
            setState((s) => ({
              ...s,
              emailReceived: true,
              emailSubject: data.subject || null,
              emailFrom: data.from_email || null,
              isExtracting: true,
              error: null,
              errorDetails: null,
            }));
            break;

          case 'inbound_email_extracted':
            setState((s) => ({
              ...s,
              isExtracting: false,
              extractedData: data.extracted_data,
              error: null,
              errorDetails: null,
            }));
            break;

          case 'inbound_email_error':
            setState((s) => ({
              ...s,
              isExtracting: false,
              error:
                data.reason === 'no_booking_found'
                  ? 'No booking confirmation was found in the email. Please try uploading the file directly or forward a different email.'
                  : data.details || `Error processing email: ${data.reason}`,
              errorDetails: {
                reason: data.reason,
                attachmentsFound: data.attachments_found || [],
              },
            }));
            break;

          case 'pong':
            // Ping/pong for keep-alive
            break;

          default:
            console.warn('[EmailNotification] Unknown message type:', data.type);
        }
      } catch (e) {
        console.error('[EmailNotification] Failed to parse message:', e);
      }
    };

    ws.onclose = (event) => {
      console.log('[EmailNotification] Disconnected:', event.code, event.reason);
      wsRef.current = null;

      // Don't reconnect if we have extracted data (success case)
      // or if we intentionally closed
      if (state.extractedData || event.code === 1000) {
        setState((s) => ({ ...s, status: 'disconnected' }));
        return;
      }

      // Attempt to reconnect with exponential backoff
      if (reconnectAttempts.current < maxReconnectAttempts) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        console.log(
          `[EmailNotification] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`
        );
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttempts.current++;
          connect();
        }, delay);
      } else {
        setState((s) => ({
          ...s,
          status: 'error',
          error: 'Connection lost. Please refresh the page.',
        }));
      }
    };

    ws.onerror = (error) => {
      console.error('[EmailNotification] WebSocket error:', error);
      setState((s) => ({ ...s, status: 'error' }));
    };
  }, [caseId, state.extractedData]);

  const disconnect = useCallback(() => {
    console.log('[EmailNotification] Disconnecting');
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
    setState((s) => ({ ...s, status: 'disconnected' }));
  }, []);

  const reset = useCallback(() => {
    disconnect();
    setState(initialState);
    reconnectAttempts.current = 0;
  }, [disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmount');
      }
    };
  }, []);

  // Send ping every 30 seconds to keep connection alive
  useEffect(() => {
    if (state.status !== 'connected') return;

    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  }, [state.status]);

  return {
    ...state,
    connect,
    disconnect,
    reset,
  };
}
